import { InitialAppearance } from "../components/Animation";
import FixedNav from "../components/Nav/FixedNav";
import MobileNav from "../components/Nav/MobileNav";
import Header from "../components/Description/Header";
import Normal from "../components/Description/Normal";
import Footer from "../components/Footer";
import { useState } from "react";
import type { FC } from 'react';

interface Job {
  title: string;
  salary: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
}

// Commented out original jobs
/*
const jobs: Job[] = [
  {
    title: "Full-Stack AI Product Engineer",
    salary: "$60,000–$90,000 USD annually",
    description: "The Backbone of Innovation",
    responsibilities: [
      "Develop scalable backend systems that power our AI platform and create seamless frontend interfaces that enhance user experience",
      "Rapidly prototype and deploy features, iterating on them based on real-world feedback",
      "Collaborate closely with our AI researchers to integrate cutting-edge technologies into the product",
      "Ensure platform stability while maintaining the agility needed to innovate at breakneck speed"
    ],
    requirements: [
      "A technical polymath with 3+ years of experience in both frontend and backend development",
      "Proficient in JavaScript (React/Node.js), Python, or similar frameworks and languages",
      "Experienced in cloud services (AWS, GCP, or Azure)",
      "A problem solver who thrives on turning complex challenges into elegant solutions"
    ]
  },
  {
    title: "Frontend Systems Architect",
    salary: "$55,000–$80,000 USD annually",
    description: "Crafting Intuitive Brilliance",
    responsibilities: [
      "Design and implement intuitive, user-centric interfaces for AI-powered workflows",
      "Build scalable, maintainable frontend systems that are as visually stunning as they are functional",
      "Own the frontend architecture, ensuring seamless integration with backend systems and AI components",
      "Collaborate with design and engineering teams to turn complex workflows into elegant solutions"
    ],
    requirements: [
      "A frontend visionary with 3+ years of experience creating exceptional user interfaces",
      "Proficient in frameworks like React, Vue, or Angular and tools like Webpack or Vite",
      "Passionate about UI/UX design principles and have a portfolio to prove it",
      "Thrives on turning complexity into simplicity for users"
    ]
  },
  {
    title: "Infrastructure Engineering Lead",
    salary: "$70,000–$100,000 USD annually",
    description: "The Foundation Builder",
    responsibilities: [
      "Architect and implement the cloud infrastructure powering thousands of concurrent AI development environments",
      "Optimize for scalability, security, and reliability in distributed systems",
      "Design CI/CD pipelines to ensure smooth deployment of updates and features",
      "Collaborate with engineering teams to anticipate infrastructure needs and prevent bottlenecks"
    ],
    requirements: [
      "A systems architecture expert with 5+ years of experience in cloud and distributed systems",
      "Proficient in Kubernetes, Docker, Terraform, and major cloud platforms (AWS, GCP, or Azure)",
      "Deep understanding of microservices architecture and DevOps practices",
      "Comfortable managing high-stakes infrastructure challenges"
    ]
  },
  {
    title: "Data & Growth Engineering Specialist",
    salary: "$60,000–$85,000 USD annually",
    description: "Turning Insights into Impact",
    responsibilities: [
      "Analyze user behavior to identify growth opportunities and product improvement strategies",
      "Build predictive models and systems that optimize user engagement and retention",
      "Develop data pipelines and visualization tools to guide decision-making",
      "Work closely with marketing and product teams to turn data into actionable insights"
    ],
    requirements: [
      "A data-driven engineer with 3+ years of experience in data analysis and engineering",
      "Proficient in Python, SQL, and data visualization tools (Tableau, Power BI)",
      "Skilled in building ETL pipelines and predictive models",
      "Passionate about using data to solve real-world problems"
    ]
  },
  {
    title: "Head of Brand Strategy & Communications",
    salary: "$55,000–$85,000 USD annually",
    description: "Defining Our Voice",
    responsibilities: [
      "Shape and execute a compelling brand narrative that bridges technical complexity with clarity",
      "Lead communications efforts, including content marketing, PR, and social media campaigns",
      "Work with design and product teams to ensure cohesive messaging across all touchpoints",
      "Establish the brand as a thought leader in AI and tech innovation"
    ],
    requirements: [
      "A storyteller with 5+ years of experience in brand strategy or communications",
      "Comfortable translating technical jargon into compelling, relatable stories",
      "Skilled in managing teams and campaigns that drive measurable results",
      "Passionate about technology and its transformative potential"
    ]
  },
  {
    title: "Digital Content & Production Director",
    salary: "$50,000–$75,000 USD annually",
    description: "Creativity Meets Tech",
    responsibilities: [
      "Develop high-quality educational and inspirational content to demystify AI development",
      "Manage multimedia production across blogs, videos, podcasts, and more",
      "Collaborate with AI researchers and engineers to create engaging technical materials",
      "Build a consistent content pipeline that resonates with our global audience"
    ],
    requirements: [
      "A creative technologist with 3+ years of experience in content creation or multimedia production",
      "Proficient in video editing, graphic design, or animation tools",
      "Strong understanding of AI/tech concepts and the ability to communicate them effectively",
      "Experienced in managing cross-functional content projects"
    ]
  }
];
*/

// New job listings
const jobs: Job[] = [
  {
    title: "Senior Software Engineer (JavaScript/TypeScript)",
    salary: "$95,000–$130,000 USD annually",
    description: "Frontend Excellence Specialist",
    responsibilities: [
      "Architect and develop sophisticated web applications using modern JavaScript and TypeScript",
      "Lead implementation of complex UI components with focus on performance and accessibility",
      "Collaborate with UX/UI designers to translate designs into responsive, interactive experiences",
      "Mentor junior developers and contribute to establishing frontend best practices and standards"
    ],
    requirements: [
      "5+ years experience with JavaScript, TypeScript, and modern frontend frameworks (React, Vue, or Angular)",
      "Strong understanding of web fundamentals including HTML5, CSS3, and browser compatibility",
      "Experience with state management, API integration, and frontend testing methodologies",
      "Excellent problem-solving skills and attention to detail"
    ]
  },
  {
    title: "Backend System Architect",
    salary: "$110,000–$150,000 USD annually",
    description: "Scalable Infrastructure Visionary",
    responsibilities: [
      "Design and implement high-performance, scalable backend systems and microservices",
      "Optimize database structures and queries for maximum efficiency at scale",
      "Lead architectural decisions for new features and system improvements",
      "Develop robust APIs and services that power our web and mobile applications"
    ],
    requirements: [
      "7+ years experience in backend development with expertise in distributed systems",
      "Proficiency in server-side languages such as Node.js, Python, Java, or Go",
      "Strong experience with database technologies (SQL and NoSQL) and data modeling",
      "Knowledge of cloud infrastructure, containerization, and CI/CD pipelines"
    ]
  }
];

const CareersComponent: FC = () => {
  const [expandedJob, setExpandedJob] = useState<number | null>(null);

  return (
    <div id="body">
      <div className="framer-IqNBt framer-vrYYp framer-NrkXy framer-l50WH framer-V74Ls framer-uKIIJ framer-Fw2Wf framer-72rtr7 min-h-screen !w-auto">
        <FixedNav />
        <MobileNav />
        <div id="content" className="w-full mt-[60px] p-[20px]">
          {/* Hero Section */}
          <InitialAppearance
            className="max-w-7xl mx-auto text-center"
            from={{ top: 0, left: 0 }}
            to={{ top: 0, left: 0 }}
            time={1}
          >
            <Header>We're Hiring: Shape the Future of AI Code Generation</Header>
            <Normal>
              Welcome to the forefront of AI innovation, where groundbreaking ideas meet flawless execution. 
              Our mission is simple yet ambitious: to redefine how the world builds software by leveraging 
              the power of artificial intelligence.
            </Normal>
            <div className="mt-4">
              <Normal>
                As part of our fully remote team, you'll have the freedom to innovate from anywhere in the world 
                while contributing to a platform that's poised to disrupt the status quo. We're not looking for 
                the ordinary—we want the extraordinary. If you're ready to be part of something transformative, 
                check out our open positions below.
              </Normal>
            </div>
          </InitialAppearance>

          <div className="p-10" />

          {/* Job Listings */}
          <div className="max-w-7xl mx-auto">
            {jobs.map((job, index) => (
              <InitialAppearance
                key={index}
                className="mb-6"
                from={{ top: 50, left: 0 }}
                to={{ top: 0, left: 0 }}
                time={1}
              >
                <div 
                  className={`bg-[#0f0f0f] border border-[#222222] rounded-lg p-6 transition-all duration-300 ${
                    expandedJob === index ? 'border-[#70bffa]' : 'hover:border-[#70bffa]'
                  }`}
                >
                  <div 
                    className="cursor-pointer"
                    onClick={() => setExpandedJob(expandedJob === index ? null : index)}
                  >
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-semibold text-white">{job.title}</h3>
                      <span className="text-[#70bffa]">{expandedJob === index ? '−' : '+'}</span>
                    </div>
                    <p className="text-gray-300 mt-2">{job.description}</p>
                  </div>
                  
                  {expandedJob === index && (
                    <div className="mt-6">
                      <div className="mb-4">
                        <h4 className="text-white font-semibold mb-2">What You'll Do:</h4>
                        <ul className="list-disc pl-6 text-gray-300 space-y-2">
                          {job.responsibilities.map((resp, i) => (
                            <li key={i}>{resp}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="mb-4">
                        <h4 className="text-white font-semibold mb-2">Who You Are:</h4>
                        <ul className="list-disc pl-6 text-gray-300 space-y-2">
                          {job.requirements.map((req, i) => (
                            <li key={i}>{req}</li>
                          ))}
                        </ul>
                      </div>
                   
                    </div>
                  )}
                </div>
              </InitialAppearance>
            ))}
          </div>

          <div className="p-10" />

          {/* Benefits */}
          <InitialAppearance
            className="text-center"
            from={{ top: 50, left: 0 }}
            to={{ top: 0, left: 0 }}
            time={1}
          >
            <Header>Why Join Us?</Header>
            <Normal>Benefits and perks that put you first</Normal>
          </InitialAppearance>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 mt-8 max-w-7xl mx-auto">
            {[
              {
                title: "Remote Work",
                description: "Work from anywhere, collaborate with the best minds globally",
              },
              {
                title: "Cutting-Edge Tech",
                description: "Be at the forefront of AI innovation",
              },
              {
                title: "Impactful Work",
                description: "Shape the tools that will define the future of software development",
              },
              {
                title: "Growth Opportunities",
                description: "Join a team that values curiosity, learning, and ambition",
              }
            ].map((benefit, index) => (
              <InitialAppearance
                key={index}
                className="bg-[#0f0f0f] border border-[#222222] rounded-lg p-6"
                from={{ top: 100, left: 0 }}
                to={{ top: 0, left: 0 }}
                time={1}
              >
                <h3 className="text-xl font-semibold mb-2 text-white">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </InitialAppearance>
            ))}
          </div>

          <div className="p-10" />

          {/* How to Apply */}
          <InitialAppearance
            className="text-center max-w-3xl mx-auto"
            from={{ top: 50, left: 0 }}
            to={{ top: 0, left: 0 }}
            time={1}
          >
            <Header>How to Apply</Header>
            <Normal>
              If you're ready to make your mark, send your resume and portfolio to{' '}
              <a 
                href="mailto:careers@codeblaze.ai"
                className="text-[#70bffa] hover:underline"
              >
                careers@codeblaze.ai
              </a>
              . Let's build the future together!
            </Normal>
          </InitialAppearance>

          <div className="p-10" />
        </div>
        <Footer />
      </div>
    </div>
  );
};

const Careers = CareersComponent;
export { Careers as default };